import { Link } from "react-router-dom";
import { useSeo } from "../components/Seo";
import { ELECTION_DATE } from "../data/site";

const standards = [
    {
        title: "Public information, clearly presented",
        text: "Candidate information is presented for public understanding — backgrounds, records of public service and stated priorities.",
    },
    {
        title: "Promises attributed, facts distinguished",
        text: "Campaign promises are attributed to the candidates and are never presented as completed municipal achievements. Biographical facts are distinguished from campaign commitments and editorial commentary.",
    },
    {
        title: "Independent and unaffiliated",
        text: "HALTONS.CA is not affiliated with any municipal government or candidate campaign, and it is not an official municipal election service.",
    },
    {
        title: "Corrections welcome",
        text: "Readers can report factual errors at any time. Profiles may be updated as new information is supplied.",
    },
    {
        title: "Nothing invented",
        text: "Where a detail has not been supplied — a date, a figure, a quotation — it is left out rather than estimated.",
    },
];

export default function About() {
    useSeo({
        title: "About & Editorial Standards — HALTONS.CA",
        description:
            "HALTONS.CA is an independent civic information publication covering the 2026 mayoral elections in Milton, Oakville, Burlington and Halton Hills. Read our editorial standards.",
        path: "/about",
    });

    return (
        <main data-testid="about-page">
            <section className="border-b border-border bg-navy text-ivory">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                        The People Shaping Halton
                    </p>
                    <h1 className="mt-4 font-serif-display text-4xl font-bold sm:text-5xl">
                        About HALTONS.CA
                    </h1>
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ivory/75 md:text-base">
                        Your Region. Your Candidates.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
                <h2 className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                    What this publication is
                </h2>
                <p className="mt-5 text-base leading-relaxed text-foreground/90">
                    HALTONS.CA is an independent civic information and editorial publication covering
                    the 2026 municipal mayoral elections in Milton, Oakville, Burlington and Halton
                    Hills. Election day is {ELECTION_DATE}.
                </p>
                <p className="mt-4 text-base leading-relaxed text-foreground/90">
                    The publication exists to help residents discover candidates, understand their
                    backgrounds, read about their public service, explore their stated policies and
                    access official election information — in one place, in plain language.
                </p>

                <h2 className="mt-14 font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                    Editorial standards
                </h2>
                <div className="mt-8 space-y-5">
                    {standards.map((s) => (
                        <div key={s.title} className="border border-border bg-white p-6">
                            <div className="h-1 w-10 bg-gold" aria-hidden="true" />
                            <h3 className="mt-4 font-serif-display text-lg font-semibold text-navy">{s.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-12 border border-teal/40 bg-teal/5 p-6">
                    <h2 className="font-serif-display text-xl font-semibold text-navy">
                        Report a factual error
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-foreground/90">
                        If you believe anything published on HALTONS.CA is factually incorrect, we want
                        to hear about it. Corrections are reviewed against supplied source material.
                    </p>
                    <Link
                        to="/corrections"
                        data-testid="about-corrections-link"
                        className="mt-5 inline-block bg-navy px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-teal"
                    >
                        Report a correction
                    </Link>
                </div>
            </section>
        </main>
    );
}
